
  import { applyLegalLang, legalLangFrom } from '/js/shared/legal-i18n.js';
  import { privacySummaryRows, privacySummaryPack } from '/js/shared/privacy-summary.js';
  import { processorRows, processorTableHeadings } from '/js/shared/processor-inventory.js';
  const DOC = document.documentElement.getAttribute('data-legal-doc') || 'privacy';
  // QUEUE ITEM BQ — two things were wrong here, and they cancelled out so neatly
  // that neither showed.
  //
  // This read `kaf_lang` / `lang`, and NOTHING in the product has ever written
  // either name — the portal's key was `kinetiqaf_lang`. So the rung the comment
  // described did not exist: a Spanish reader's saved preference never reached
  // this page, and the only reason it did not read as a bug is that the portal
  // ALSO appends `?lang=` when it links here (see the Settings rows in
  // app.html), and `?lang=` wins. The dead read was doing nothing at all.
  //
  // What it reads now is the PUBLIC pages' own key, which is what these pages
  // are: a stranger who chose Español on the intake and then opened the privacy
  // policy from its footer keeps reading Spanish. A signed-in client's language
  // still arrives on the URL, from their own profile, which is the only place
  // an account's language is allowed to come from.
  let stored = null;
  try { stored = localStorage.getItem('kinetiqaf_public_lang'); } catch (_) {
    // Storage blocked (private mode) — ?lang= and the browser still decide.
  }
  let lang = legalLangFrom(location.search, navigator.language, stored);
  // The switches always point at the OTHER language, and keep the hash so a
  // reader deep in §9 stays in §9 when they switch. Queue item KW (E-01): the
  // header's and the footer's both, named in their own language, and a press
  // repaints in place (applyLegalLang only swaps words) rather than reloading.
  function syncSwitches() {
    const other = lang === 'es' ? 'en' : 'es';
    document.querySelectorAll('[data-legal-lang-switch]').forEach((a) => {
      const to = a.getAttribute('data-legal-lang-switch') || other;
      a.setAttribute('href', '?lang=' + to + location.hash);
      a.setAttribute('hreflang', to);
      if (!a.getAttribute('data-legal-lang-switch')) a.setAttribute('lang', to);
    });
  }
  function paint() {
    applyLegalLang(document, lang, DOC);
    syncSwitches();
    paintRest();
  }
  document.querySelectorAll('[data-legal-lang-switch]').forEach((a) => a.addEventListener('click', (e) => {
    e.preventDefault();
    lang = a.getAttribute('data-legal-lang-switch') || (lang === 'es' ? 'en' : 'es');
    try { localStorage.setItem('kinetiqaf_public_lang', lang); } catch (_) { /* storage blocked: the URL still carries it */ }
    try {
      const url = new URL(location.href);
      url.searchParams.set('lang', lang);
      history.replaceState(history.state, '', url.toString());
    } catch (_) { /* an older browser: the repaint below still happens */ }
    paint();
  }));

  // Queue item BT item 1 — §5's processor table, rendered from the inventory
  // the build gate reads. The page holds no list of its own, so there is no
  // second copy to fall behind: adding a processor to the code without adding it
  // here is not a documentation lapse anybody has to notice, it is a failing
  // build. Written with createElement + textContent rather than innerHTML —
  // these strings are authored source, but a table built by concatenation is
  // how the next person's paste becomes markup.
  // Queue item BT — the stacked layout needs each cell to know its column's
  // name, and §5a's labels are written into the markup in English as a
  // pre-paint fallback. Once applyLegalLang has swapped the headings, copy them
  // back down the columns so a Spanish reader on a phone gets Spanish labels —
  // the alternative is eight more pack keys saying what four <th> elements
  // already say.
  // Everything below depends on the language, so a switch re-runs it.
  function paintRest() {
    document.querySelectorAll('table.stacked').forEach((tbl) => {
      const heads = [...tbl.querySelectorAll('thead th')].map((th) => th.textContent.trim());
      if (!heads.length) return;
      tbl.querySelectorAll('tbody tr').forEach((tr) => {
        [...tr.children].forEach((td, n) => {
          if (heads[n]) td.setAttribute('data-label', heads[n]);
        });
      });
    });

    const ptable = document.getElementById('processor-table');
    if (ptable) {
      const head = processorTableHeadings(lang);
      const table = document.createElement('table');
      // `.stacked` — on a phone this becomes one labelled block per processor
      // rather than four columns off the right edge. See the media query.
      table.className = 'stacked';
      const thead = document.createElement('thead');
      const hrow = document.createElement('tr');
      [['role', '26%'], ['receives', '38%'], ['why', '22%'], ['status', '14%']].forEach(([k, w]) => {
        const th = document.createElement('th');
        th.style.width = w;
        th.textContent = head[k];
        hrow.appendChild(th);
      });
      thead.appendChild(hrow);
      table.appendChild(thead);
      const tbody = document.createElement('tbody');
      processorRows(lang).forEach((r) => {
        const tr = document.createElement('tr');
        tr.setAttribute('data-processor', r.id);
        ['role', 'receives', 'why', 'statusLabel'].forEach((k, n) => {
          const td = document.createElement('td');
          // The column name travels with the cell, so the stacked layout can put
          // it back above the value in the reader's own language.
          td.setAttribute('data-label', head[['role', 'receives', 'why', 'status'][n]]);
          td.textContent = r[k];
          if (k === 'statusLabel' && r.status === 'dormant') td.style.color = 'var(--steel)';
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      ptable.replaceChildren(table);
    }

    // Queue item AQ item 2 — the plain-language summary, from the SAME module the
    // portal's Privacy & Data tab renders. Neither file carries a copy of these
    // sentences; META-12 fails the build if one appears, because a pasted copy is
    // exactly how a summary and the policy it summarises stop agreeing.
    const mount = document.getElementById('plain-language');
    if (mount) {
      const pack = privacySummaryPack(lang);
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      mount.innerHTML =
        '<div class="plain-title">' + esc(pack['sum.title']) + '</div>' +
        '<p class="plain-intro">' + esc(pack['sum.intro']) + '</p>' +
        privacySummaryRows(lang).map((r) =>
          '<div class="plain-row"><div class="plain-q">' + esc(r.q) + '</div>' +
          '<p class="plain-a">' + esc(r.a) +
          '<a class="inline plain-ref" href="#' + esc(r.anchor) + '">' + esc(r.sectionLabel) + '</a></p></div>'
        ).join('');
    }
  }
  paint();
  window.addEventListener('hashchange', syncSwitches);
